/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Modal } from "components/generals/Modal";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import PermissionWrapper from "components/generals/PermissionWrapper";
import DataTable, { Props } from "dashboard-template/examples/Tables/DataTable";
import { HeaderEnum, PersonInterface, UserInterface } from "medicalsystem-shared";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import UserFullForm from "components/forms/User/UserFullForm";
import { TableProps } from "interfaces";

/**
 * User table component for displaying and managing system user accounts.
 * 
 * Features:
 * - Displays user account information in sortable data table
 * - Columns: username, email, roles (comma-separated), action buttons
 * - View/edit user functionality via modal with UserFullForm
 * - Delete user with confirmation dialog and list update
 * - Fetches associated person data when viewing/editing user
 * - Permission-based access control for all operations
 * - Role display with array-to-string conversion
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure user identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * - Fetches person data via PERSONS_GET_BY_USER_ID for complete profile
 * 
 * Workflow:
 * 1. User clicks edit button
 * 2. System fetches associated person data by user ID
 * 3. Modal opens with complete user+person information
 * 4. Changes saved via UserFullForm
 * 5. Table updates with modified user data
 * 
 * @param {TableProps<UserInterface>} props - Table properties
 * @param {UserInterface[]} props.modelList - Array of user records to display
 * @param {Function} props.setModelList - Callback to update user list after changes
 * 
 * @returns {JSX.Element} User data table with view, edit, and delete actions
 */
const UserTable: FC<TableProps<UserInterface>> = (props) => {
  const { t } = useTranslation();
  const [activeEditUserModal, setActiveEditUserModal] = useState<boolean>(false);
  const [person, setPerson] = useState<PersonInterface>();
  const [user, setUser] = useState<UserInterface>();

  const [_isLoading, _error, sendRequest] = useHttp();

  const deleteHandler = (userTemplate: UserInterface) => {
    sendRequest<boolean, UserInterface>(
      CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
      onUserDeleteHandler,
      "DELETE",
      null,
      [{ header: HeaderEnum.RESOURCE_ID, value: userTemplate.id }],
      { payload: userTemplate, successMessage: t("userTable.userDeleted") }
    );
  };
  
  const onUserDeleteHandler = (result: boolean, userDeleted: UserInterface) => {
    const indexList = props.modelList.indexOf(userDeleted);
    props.modelList.splice(indexList, 1);
    props.setModelList([...props.modelList]);
  };

  const selectUser = async (user: UserInterface) => {
    const response = await sendRequest<{ results: PersonInterface }>(
      CoreAPI_URLs.PERSONS_GET_BY_USER_ID,
      (response) => {
        if (response) {
          setPerson(response.results);
        }
      },
      "GET",
      null,
      [{ header: HeaderEnum.RESOURCE_ID, value: user.id }]
    );
    if (response && !(response instanceof Error)) {
      setPerson(response.data);
    }
    setActiveEditUserModal(true);
  };

  const getUITable = (list: UserInterface[]) => {
    return {
      columns: [
        {
          Header: t("userTable.userName"),
          accessor: "username",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("userTable.email"),
          accessor: "email",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("userTable.roles"),
          accessor: "roles",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => (
            <DefaultCell
              value={
                Array.isArray(value)
                  ? value.map((item) => t(`userTable.${item}`)).join(", ")
                  : t(`userTable.${value}`)
              }
            />
          ),
        },
        {
          Header: t("userTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewUserButton"
                variant="contained"
                color="info"
                sx={{ marginRight: 1 }}
                onClick={() => {
                  const userClick = list.find((p) => p.id === value);
                  if (userClick) {
                    setUser(userClick);
                    selectUser(userClick);
                  }
                }}
              >
                {t("userTable.view")}
              </Button>

              {/* <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.USERS].DELETE]}
              >
                <ButtonDialog
                  name="deleteUserButton"
                  onAcceptHandle={() => {
                    const userToDelete = list.find((p) => p.id === value);
                    if (userToDelete) {
                      deleteHandler(userToDelete);
                    }
                  }}
                  buttonText={t("userTable.deleteAction")}
                  title={t("userTable.deleteConfirmation")}
                  description={t("userTable.deleteDescription")}
                  labelledbyID={"UserDelete"}
                  describedbyID={"UserDescriptionId"}
                />
              </PermissionWrapper> */}
              <PermissionWrapper
  requiredPermissions={[PermissionsUser[EntityPermissions.USERS].DELETE]}
>
  <ButtonDialog
    name="deleteUserButton"
    onAcceptHandle={() => {
      const userToDelete = list.find((p) => p.id === value);
      if (userToDelete) {
        deleteHandler(userToDelete);
      }
    }}
    buttonText={t("userTable.deleteAction")}
    title={t("userTable.deleteConfirmation")}
    description={t("userTable.deleteDescription")}
    labelledbyID={"UserDelete"}
    describedbyID={"UserDescriptionId"}
  />
</PermissionWrapper>
            </>
          ),
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(props.modelList), [props.modelList]);

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>
      <Modal
        open={activeEditUserModal}
        onClose={() => {
          setActiveEditUserModal(false);
        }}
      >
        <Box>
          <UserFullForm
            isModal={true}
            model={user}
            modelList={props.modelList}
            setModelList={props.setModelList}
            action={FormActionType.MODIFY}
            onCloseAction={() => setActiveEditUserModal(false)}
          />
        </Box>
      </Modal>
    </>
  );
};

export default UserTable;